import { apiClient } from "./apiClient";
import { LoginCredentials } from "@/types/auth";
import logger from "@/utils/logger";

// ─── Types ──────────────────────────────────────────────────────────────

export interface FacultyUser {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    user_role: string;
}

export interface FacultyLoginResponse {
    success: number;
    message: string;
    require_otp?: boolean;
    user_id?: string;
    pending?: string[];
    otp_channel?: "email" | "mobile";
    mobile?: string;
    email?: string;
    data?: {
        token: string;
        user: FacultyUser;
    };
}

export interface FacultyDashboardData {
    faculty: {
        first_name: string;
        last_name: string;
        avatar: string;
        email: string;
        mobile: string | null;
        department: string | null;
        designation: string;
        experience_years: number;
        avg_rating: number;
        total_students: number;
        verified: boolean;
    };
    stats: Array<{
        icon: string;
        title: string;
        value: string;
        color: string;
        bg: string;
        trend?: { direction: "up" | "down"; label: string };
    }>;
    performance: {
        categories: Array<{ name: string; value: number; color: string }>;
    };
    batches: Array<{
        id: string;
        name: string;
        students: number;
        avg_score: number;
        attendance: number;
        progress: number;
        icon: string;
        color: string;
        bg: string;
    }>;
    attention_students: Array<{
        id: string;
        name: string;
        course: string;
        issue: string;
        issue_type: "critical" | "warning" | "info";
        avatar: string;
    }>;
    training_progress: Array<{ id: string; name: string; value: number }>;
    exam_analytics: {
        total_students: number;
        passed: number;
        failed: number;
        highest_score: number;
        chart_data: Array<{ name: string; value: number; color: string }>;
    };
    practice: {
        questions_solved_today: number;
        top_performers: Array<{ rank: number; name: string; score: string; avatar: string }>;
    };
    versant: {
        completed: number;
        pending: number;
        avg_score: number;
        below_target: number;
        chart_data: Array<{ name: string; value: number; color: string }>;
    };
    schedule: Array<{ id: string; time: string; title: string; room: string; status: string }>;
    activities: Array<{ id: string; text: string; time: string; time_ago: string; icon: string; color: string; bg: string }>;
    notifications: { unread_count: number };
}

export interface BatchYear {
    uuid: string;
    name: string;
}

export interface Batch {
    batch_id: string;
    batch_name: string;
    batch_year_id: string;
    batch_year_name: string;
}

export interface BatchByYear {
    uuid: string;
    name: string;
    batch_year_id: string;
}

export interface FacultyStudent {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    user_role?: string;
    regd: string;
    mobile: string;
    batch_id?: string;
    institution_id?: string;
    batch_year_id?: string;
    batch_name?: string;
    batch_year_name?: string;
    college?: string;
    institute_name?: string;
}

export interface FacultyAssessment {
    id: string;
    name: string;
    password?: string;
    ques_selection?: string;
    category: string;
    attempts: number;
}

export interface AssessmentReportExam {
    id?: number;
    exam_id?: string;
    user_id?: string;
    attempt_id?: string;
    status?: string;
    percentage?: number;
    marks?: number;
    total_marks?: number;
    correct?: number;
    wrong?: number;
    time_spent?: number;
    qualified?: string;
    basic_proc?: number;
    first_name?: string;
    last_name?: string;
    email?: string;
    regd?: string;
    batch?: string;
    batch_year?: string;
}

export interface AssessmentReportResponse {
    success: number;
    message: string;
    exam_details?: {
        id: string;
        name: string;
        category_name: string;
        basic_proc?: number;
    };
    exams?: AssessmentReportExam[];
    attempted?: number;
    completed?: number;
    inprogress?: number;
    qualified?: number;
    not_qualified?: number;
    highest?: number | string;
}

export interface UserAssessmentResult {
    id?: number;
    exam_id?: string;
    user_id?: string;
    attempt_id?: string;
    status?: string;
    percentage?: number;
    marks?: number;
    total_marks?: number;
    correct?: number;
    wrong?: number;
    accuracy?: number;
    time_spent?: number;
    time_spent_readable?: string;
    qualified?: string;
    name?: string;
    exam_type?: string;
    cutoff_type?: string;
    results?: any[];
}

export interface UserAssessmentReportResponse {
    success: number;
    user: FacultyStudent;
    result: UserAssessmentResult[];
}

// ─── Exam Access Types ──────────────────────────────────────────────

export interface ExamAccessUser {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    regd: string;
    batch_id: string;
    exam_id: string;
    access: number;
}

export interface ExamAccessUsersResponse {
    success: number;
    message: string;
    data: ExamAccessUser[];
}

export interface GrantAccessResponse {
    success: number;
    message: string;
    inserted_count?: number;
    updated_count?: number;
    revoked_count?: number;
    granted_count?: number;
    total_found?: number;
}

export interface Batch {
    batch_id: string;
    batch_name: string;
    batch_year_id: string;
    batch_year_name: string;
}

// ─── Service ────────────────────────────────────────────────────────────

class FacultyService {

    /**
     * Get KPI comparison for faculty dashboard
     */
    async getKpiComparison(params?: {
        date1?: string;
        date2?: string;
        passoutYear?: string;
        department?: string;
        batchId?: string;
    }): Promise<any> {
        try {
            const searchParams = new URLSearchParams();
            if (params) {
                Object.entries(params).forEach(([key, value]) => {
                    if (value !== undefined) {
                        searchParams.append(key, value);
                    }
                });
            }
            const qs = searchParams.toString() ? `?${searchParams.toString()}` : '';
            return await apiClient.get(`/faculty/analytics/kpi-comparison${qs}`);
        } catch (error) {
            logger.error('Error fetching KPI comparison for faculty:', error);
            throw error;
        }
    }

    /**
     * Get Passout Years with Batches for Faculty
     */
    async getPassoutYearsWithBatches(): Promise<any> {
        try {
            return await apiClient.get('/faculty/passout-years/full');
        } catch (error) {
            logger.error('Error fetching passout years with batches for faculty:', error);
            throw error;
        }
    }

    /**
     * Fetch assessments drilldown reports for faculty
     */
    async getAssessmentsDrilldown(payload: Record<string, any>): Promise<any> {
        try {
            return await apiClient.post('/faculty/analytics/assessments/drilldown', payload);
        } catch (error) {
            logger.error('Error fetching faculty assessments drilldown:', error);
            throw error;
        }
    }

    /**
     * Fetch practice drilldown reports for faculty
     */
    async getPracticeDrilldown(payload: Record<string, any>): Promise<any> {
        try {
            return await apiClient.post('/faculty/analytics/practice/drilldown', payload);
        } catch (error) {
            logger.error('Error fetching faculty practice drilldown:', error);
            throw error;
        }
    }

    /**
     * Fetch courses drilldown reports for faculty
     */
    async getCoursesDrilldown(payload: Record<string, any>): Promise<any> {
        try {
            return await apiClient.post('/faculty/analytics/courses/drilldown', payload);
        } catch (error) {
            logger.error('Error fetching faculty courses drilldown:', error);
            throw error;
        }
    }

    /**
     * Fetch versant drilldown reports for faculty
     */
    async getVersantDrilldown(payload: Record<string, any>): Promise<any> {
        try {
            return await apiClient.post('/faculty/analytics/versant/drilldown', payload);
        } catch (error) {
            logger.error('Error fetching faculty versant drilldown:', error);
            throw error;
        }
    }

    /**
     * Fetch exam attempts drilldown reports for faculty
     */
    async getExamAttemptsDrilldown(payload: Record<string, any>): Promise<any> {
        try {
            return await apiClient.post('/faculty/analytics/attempts/drilldown', payload);
        } catch (error) {
            logger.error('Error fetching faculty exam attempts drilldown:', error);
            throw error;
        }
    }

    /**
     * Fetch qualified students drilldown reports for faculty
     */
    async getQualifiedStudentsDrilldown(payload: Record<string, any>): Promise<any> {
        try {
            return await apiClient.post('/faculty/analytics/qualified/drilldown', payload);
        } catch (error) {
            logger.error('Error fetching faculty qualified students drilldown:', error);
            throw error;
        }
    }

    /**
     * Fetch MCQ questions drilldown reports for faculty
     */
    async getMcqQuestionsDrilldown(payload: Record<string, any>): Promise<any> {
        try {
            return await apiClient.post('/faculty/analytics/questions/mcq/drilldown', payload);
        } catch (error) {
            logger.error('Error fetching faculty MCQ questions drilldown:', error);
            throw error;
        }
    }

    /**
     * Fetch coding questions drilldown reports for faculty
     */
    async getCodingQuestionsDrilldown(payload: Record<string, any>): Promise<any> {
        try {
            return await apiClient.post('/faculty/analytics/questions/coding/drilldown', payload);
        } catch (error) {
            logger.error('Error fetching faculty coding questions drilldown:', error);
            throw error;
        }
    }

    /**
     * Fetch coding submissions drilldown reports for faculty
     */
    async getCodingSubmissionsDrilldown(payload: Record<string, any>): Promise<any> {
        try {
            return await apiClient.post('/faculty/analytics/submissions/drilldown', payload);
        } catch (error) {
            logger.error('Error fetching faculty coding submissions drilldown:', error);
            throw error;
        }
    }

    /**
     * Fetch CodeArena drilldown reports for faculty
     */
    async getCodeArenaDrilldown(payload: Record<string, any>): Promise<any> {
        try {
            return await apiClient.post('/faculty/analytics/codearena/drilldown', payload);
        } catch (error) {
            logger.error('Error fetching faculty CodeArena drilldown:', error);
            throw error;
        }
    }

    /**
     * Dynamic unified dispatcher for faculty drilldowns
     */
    async getHistoricalDrilldown(payload: Record<string, any>): Promise<any> {
        try {
            return await apiClient.post('/faculty/analytics/drilldown', payload);
        } catch (error) {
            logger.error('Error fetching faculty historical drilldown:', error);
            throw error;
        }
    }

    /**
     * Fetch course excel report for faculty
     */
    async getCourseExcelReport(payload: Record<string, any>): Promise<any> {
        try {
            return await apiClient.post('/faculty/reports/course-excel', payload);
        } catch (error) {
            logger.error('Error fetching course excel report for faculty:', error);
            throw error;
        }
    }

    /**
     * Download course report with filters for faculty
     */
    async downloadCourseReport(payload: Record<string, any>): Promise<any> {
        try {
            return await apiClient.post('/faculty/reports/learningpath/download', payload);
        } catch (error) {
            logger.error('Error downloading course report for faculty:', error);
            throw error;
        }
    }


    /**
     * Faculty login
     */
    async login(credentials: LoginCredentials): Promise<FacultyLoginResponse> {
        try {
            return await apiClient.post("/faculty/login", credentials);
        } catch (error) {
            logger.error("Faculty login error:", error);
            throw error;
        }
    }

    /**
     * Verify OTP for login
     */
    async verifyLoginOTP(data: {
        user_id: string;
        otpCode: string;
        latitude?: number;
        longitude?: number;
        accuracy?: number;
    }): Promise<FacultyLoginResponse & { require_otp?: boolean; user_id?: string }> {
        try {
            return await apiClient.post("/faculty/verify-login-otp", data);
        } catch (error) {
            logger.error("Error verifying login OTP:", error);
            throw error;
        }
    }

    /**
     * Generate a unique Telegram deep link for account linking
     */
    async generateTelegramLink(): Promise<{ success: number; message: string; data?: { link: string; } }> {
        try {
            return await apiClient.get("/faculty/telegram/generate-link");
        } catch (error) {
            logger.error("Error generating Telegram link:", error);
            throw error;
        }
    }

    /**
     * Get Telegram connection status
     */
    async getTelegramStatus(): Promise<{ success: number; message: string; data?: { is_linked: boolean; telegram_chat_id?: string; } }> {
        try {
            return await apiClient.get("/faculty/telegram/status");
        } catch (error) {
            logger.error("Error fetching Telegram status:", error);
            throw error;
        }
    }

    /**
     * Logout faculty
     */
    async logout(): Promise<void> {
        if (typeof window !== "undefined") {
            localStorage.removeItem("token");
            localStorage.removeItem("isAuthenticated");
            localStorage.removeItem("userRole");
            localStorage.removeItem("userEmail");
            localStorage.removeItem("userId");
            localStorage.removeItem("facultyAuthenticated");
            localStorage.removeItem("userName");
            localStorage.removeItem("faculty-template");
        }
    }

    /**
     * Check if faculty is authenticated
     */
    isAuthenticated(): boolean {
        if (typeof window === "undefined") return false;
        const token = localStorage.getItem("token");
        const role = localStorage.getItem("userRole");
        return !!token && role === "faculty";
    }

    /**
     * Get stored auth token
     */
    getToken(): string | null {
        if (typeof window === "undefined") return null;
        return localStorage.getItem("token");
    }

    /**
     * Store authentication data
     */
    setAuthData(data: { token: string; user: FacultyUser }): void {
        if (typeof window !== "undefined") {
            localStorage.setItem("token", data.token);
            localStorage.setItem("isAuthenticated", "true");
            localStorage.setItem("userRole", "faculty");
            localStorage.setItem("userEmail", data.user.email);
            localStorage.setItem("userId", data.user.id);
            localStorage.setItem("facultyAuthenticated", "true");
            localStorage.setItem(
                "userName",
                `${data.user.first_name} ${data.user.last_name}`,
            );
        }
    }

    // ─── Dashboard ──────────────────────────────────────────────────────

    async getDashboard(): Promise<{
        success: number;
        message: string;
        cached?: boolean;
        data: FacultyDashboardData;
    }> {
        try {
            return await apiClient.get("/faculty/dashboard");
        } catch (error) {
            logger.error("Error fetching faculty dashboard:", error);
            throw error;
        }
    }

    async refreshDashboard(): Promise<{
        success: number;
        message: string;
        cached?: boolean;
        data: FacultyDashboardData;
    }> {
        try {
            return await apiClient.get("/faculty/dashboard/refresh");
        } catch (error) {
            logger.error("Error refreshing faculty dashboard:", error);
            throw error;
        }
    }

    // ─── Metrics ───────────────────────────────────────────────────────

    async getMetrics(): Promise<{
        success: number;
        message?: string;
        data: {
            faculty_name: string;
            first_name?: string;
            last_name?: string;
            department?: string;
            designation?: string;
            avg_rating?: number;
            total_students: number;
            total_batches: number;
            active_courses: number;
            exams_conducted: number;
            practice_sessions: number;
            versant_assessments: number;
            exams_attempted: number;
            questions_attempted: number;
            health_score: number;
        };
    }> {
        try {
            return await apiClient.get("/faculty/metrics");
        } catch (error) {
            logger.error("Error fetching faculty metrics:", error);
            throw error;
        }
    }

    // ─── Module Analytics ─────────────────────────────────────────────

    async getModuleAnalytics(filter?: {
        passout_year_id?: string;
        batch_id?: string;
        start_date?: string;
        end_date?: string;
    }): Promise<{
        success: number;
        data: {
            exams: {
                total_exams: number;
                average_score: number;
                pass_rate: number;
                participation_rate: number;
                trend: Array<{ date: string; value: number }>;
                top_exams: Array<{ name: string; score: number }>;
            };
            practice: {
                sessions_count: number;
                accuracy_rate: number;
                questions_attempted: number;
                topic_performance: Array<{ topic: string; accuracy: number }>;
                trend: Array<{ date: string; value: number }>;
            };
            courses: {
                enrollments: number;
                completion_rate: number;
                new_this_month: number;
                top_courses: Array<{ name: string; progress: number }>;
            };
            trainings: {
                attendance_rate: number;
                completion_rate: number;
                feedback_score: number;
                trend: Array<{ date: string; value: number }>;
            };
            versant: {
                listening: number;
                speaking: number;
                reading: number;
                writing: number;
                assessments: number;
                students_assessed: number;
                improvement: number;
                score_distribution: Array<{ range: string; count: number }>;
            };
        };
    }> {
        try {
            const searchParams = new URLSearchParams();
            if (filter) {
                Object.entries(filter).forEach(([key, value]) => {
                    if (value !== undefined) {
                        searchParams.append(key, value);
                    }
                });
            }
            const qs = searchParams.toString() ? `?${searchParams.toString()}` : '';
            return await apiClient.get(`/faculty/analytics/module${qs}`);
        } catch (error) {
            logger.error("Error fetching faculty module analytics:", error);
            throw error;
        }
    }

    async getExamsModuleAnalytics(filter?: {
        passout_year_id?: string;
        batch_id?: string;
        start_date?: string;
        end_date?: string;
    }): Promise<{
        success: number;
        data: {
            total_exams: number;
            average_score: number;
            pass_rate: number;
            participation_rate: number;
            trend: Array<{ date: string; value: number }>;
            top_exams: Array<{ name: string; score: number }>;
        };
    }> {
        try {
            return await apiClient.post("/faculty/analytics/exams", filter || {});
        } catch (error) {
            logger.error("Error fetching exams module analytics:", error);
            throw error;
        }
    }

    async getPracticeModuleAnalytics(filter?: {
        passout_year_id?: string;
        batch_id?: string;
        start_date?: string;
        end_date?: string;
    }): Promise<{
        success: number;
        data: {
            sessions_count: number;
            accuracy_rate: number;
            questions_attempted: number;
            topic_performance: Array<{ topic: string; accuracy: number }>;
            trend: Array<{ date: string; value: number }>;
        };
    }> {
        try {
            return await apiClient.post("/faculty/analytics/practice", filter || {});
        } catch (error) {
            logger.error("Error fetching practice module analytics:", error);
            throw error;
        }
    }

    async getCoursesModuleAnalytics(filter?: {
        passout_year_id?: string;
        batch_id?: string;
        start_date?: string;
        end_date?: string;
    }): Promise<{
        success: number;
        data: {
            enrollments: number;
            completion_rate: number;
            new_this_month: number;
            top_courses: Array<{ name: string; progress: number }>;
        };
    }> {
        try {
            return await apiClient.post("/faculty/analytics/courses", filter || {});
        } catch (error) {
            logger.error("Error fetching courses module analytics:", error);
            throw error;
        }
    }

    async getTrainingsModuleAnalytics(filter?: {
        passout_year_id?: string;
        batch_id?: string;
        start_date?: string;
        end_date?: string;
    }): Promise<{
        success: number;
        data: {
            attendance_rate: number;
            completion_rate: number;
            feedback_score: number;
            trend: Array<{ date: string; value: number }>;
        };
    }> {
        try {
            return await apiClient.post("/faculty/analytics/trainings", filter || {});
        } catch (error) {
            logger.error("Error fetching trainings module analytics:", error);
            throw error;
        }
    }

    async getVersantModuleAnalytics(filter?: {
        passout_year_id?: string;
        batch_id?: string;
        start_date?: string;
        end_date?: string;
    }): Promise<{
        success: number;
        data: {
            listening: number;
            speaking: number;
            reading: number;
            writing: number;
            assessments: number;
            students_assessed: number;
            improvement: number;
            score_distribution: Array<{ range: string; count: number }>;
        };
    }> {
        try {
            return await apiClient.post("/faculty/analytics/versant", filter || {});
        } catch (error) {
            logger.error("Error fetching versant module analytics:", error);
            throw error;
        }
    }

    // ─── Batch Years ────────────────────────────────────────────────────

    async getBatchYears(): Promise<{
        success: number;
        message: string;
        data: BatchYear[];
    }> {
        try {
            return await apiClient.get("/faculty/batch-years");
        } catch (error) {
            logger.error("Error fetching batch years:", error);
            throw error;
        }
    }

    // ─── Batches ────────────────────────────────────────────────────────

    async getBatchesByYear(yearId: string): Promise<{
        success: number;
        message: string;
        data: BatchByYear[];
    }> {
        try {
            return await apiClient.get(`/faculty/batches/${yearId}`);
        } catch (error) {
            logger.error("Error fetching batches by year:", error);
            throw error;
        }
    }

    async getAllBatches(): Promise<{
        success: number;
        message: string;
        data: Batch[];
    }> {
        try {
            return await apiClient.get("/faculty/all-batches");
        } catch (error) {
            logger.error("Error fetching all batches:", error);
            throw error;
        }
    }

    // ─── Users / Students ──────────────────────────────────────────────

    async searchUsers(key: string): Promise<{
        success: number;
        message: string;
        data: FacultyStudent[];
    }> {
        try {
            return await apiClient.get(
                `/faculty/user/search?key=${encodeURIComponent(key)}`,
            );
        } catch (error) {
            logger.error("Error searching users:", error);
            throw error;
        }
    }

    async getUsersByBatch(batchId: string): Promise<{
        success: number;
        message: string;
        data: FacultyStudent[];
    }> {
        try {
            return await apiClient.get(`/faculty/users/batch?batchId=${batchId}`);
        } catch (error) {
            logger.error("Error fetching users by batch:", error);
            throw error;
        }
    }

    async getUsersByBatchYear(yearId: string): Promise<{
        success: number;
        message: string;
        data: FacultyStudent[];
    }> {
        try {
            return await apiClient.get(`/faculty/users/batch-year?yearId=${yearId}`);
        } catch (error) {
            logger.error("Error fetching users by batch year:", error);
            throw error;
        }
    }

    async getUserById(id: string): Promise<{
        success: number;
        message: string;
        data: FacultyStudent;
    }> {
        try {
            return await apiClient.get(`/faculty/user/${id}`);
        } catch (error) {
            logger.error("Error fetching user by ID:", error);
            throw error;
        }
    }

    async getUserLogs(id: string, limit = 100): Promise<{
        success: number;
        message: string;
        data: any[];
    }> {
        try {
            return await apiClient.get(`/faculty/user/${id}/logs?limit=${limit}`);
        } catch (error) {
            logger.error("Error fetching user logs:", error);
            throw error;
        }
    }

    async updateUserPassword(data: {

        userId: string;
        password: string;
        institutionId: string;
    }): Promise<{
        success: number;
        message: string;
    }> {
        try {
            return await apiClient.post("/faculty/users/update-password", data);
        } catch (error) {
            logger.error("Error updating user password:", error);
            throw error;
        }
    }

    async toggleUserStatus(data: { id: string }): Promise<{
        success: number;
        message: string;
        status?: number;
    }> {
        try {
            return await apiClient.post("/faculty/user/toggle-status", data);
        } catch (error) {
            logger.error("Error toggling user status:", error);
            throw error;
        }
    }

    async updatePasswordsAtBatchLevel(data: {
        batch_year_id?: string;
        batch_id?: string;
        password: string;
    }): Promise<{
        success: number;
        message: string;
        affected_users_count?: number;
    }> {
        try {
            return await apiClient.post("/faculty/users/update-password", data);
        } catch (error) {
            logger.error("Error updating batch passwords:", error);
            throw error;
        }
    }

    // ─── Assessments ────────────────────────────────────────────────────

    async getAssessments(): Promise<{
        success: number;
        message: string;
        data: FacultyAssessment[];
    }> {
        try {
            return await apiClient.get("/faculty/assessments");
        } catch (error) {
            logger.error("Error fetching assessments:", error);
            throw error;
        }
    }

    async getAssessmentReport(
        examId: string,
    ): Promise<AssessmentReportResponse> {
        try {
            return await apiClient.get(`/faculty/assessments/report/${examId}`);
        } catch (error) {
            logger.error("Error fetching assessment report:", error);
            throw error;
        }
    }

    async getAssessmentReportData(id: string, filters: any = {}): Promise<any> {
        try {
            const queryParams = new URLSearchParams();
            if (filters.batch_year) queryParams.append("batch_year", filters.batch_year);
            if (filters.batch) queryParams.append("batch", filters.batch);

            const qs = queryParams.toString() ? `?${queryParams.toString()}` : "";
            return await apiClient.get(`/faculty/reports/assessment-report-data/${id}${qs}`);
        } catch (error) {
            logger.error("Error fetching faculty assessment report data:", error);
            throw error;
        }
    }

    async getIndividualStudentReport(examId: string, userId: string): Promise<any> {
        try {
            return await apiClient.get(`/faculty/reports/individual-student-report?exam_id=${examId}&user_id=${userId}`);
        } catch (error) {
            logger.error("Error fetching faculty individual student report:", error);
            throw error;
        }
    }

    async getAssessmentReportFullData(id: string, filters: any = {}): Promise<any> {
        try {
            const queryParams = new URLSearchParams();
            if (filters.batch_year) queryParams.append("batch_year", filters.batch_year);
            if (filters.batch) queryParams.append("batch", filters.batch);

            const qs = queryParams.toString() ? `?${queryParams.toString()}` : "";
            return await apiClient.get(`/faculty/reports/assessment-report-full-data/${id}${qs}`);
        } catch (error) {
            logger.error("Error fetching faculty assessment report full data:", error);
            throw error;
        }
    }

    async getAssessmentReportTabData(id: string, tab: string, filters: any = {}): Promise<any> {
        try {
            const queryParams = new URLSearchParams();
            if (filters.batch_year_id || filters.batch_year) queryParams.append("batch_year_id", filters.batch_year_id || filters.batch_year);
            if (filters.batch_id || filters.batch) queryParams.append("batch_id", filters.batch_id || filters.batch);
            if (filters.learning_path_uuid) queryParams.append("learning_path_uuid", filters.learning_path_uuid);
            if (filters.topic_uuid) queryParams.append("topic_uuid", filters.topic_uuid);
            if (filters.content_uuid) queryParams.append("content_uuid", filters.content_uuid);

            const qs = queryParams.toString() ? `?${queryParams.toString()}` : "";
            return await apiClient.get(`/faculty/reports/assessment-report-tab-data/${id}/${tab}${qs}`);
        } catch (error) {
            logger.error("Error fetching faculty assessment report tab data:", error);
            throw error;
        }
    }

    async getAssessmentLiveCount(assessmentId: string): Promise<{ success: number; count: number }> {
        return apiClient.get(`/faculty/reports/assessment-live-count/${assessmentId}`);
    }

    async autoSubmitAttempts(assessmentId: string): Promise<{ success: number; message: string }> {
        return apiClient.post(`/faculty/reports/assessment-auto-submit/${assessmentId}`, {});
    }

    async getUserAssessmentReport(
        examId: string,
        studentId: string,
    ): Promise<UserAssessmentReportResponse> {
        try {
            return await apiClient.get(
                `/faculty/assessments/user-report?examId=${examId}&studentId=${studentId}`,
            );
        } catch (error) {
            logger.error("Error fetching user assessment report:", error);
            throw error;
        }
    }

    async getExcelAssessmentReport(data: any): Promise<any> {
        try {
          if (data.download === true) {
            const blob = await apiClient.postBlob(`/faculty/reports/assessment-excel`, data);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            const examName = "Assessment_Report";
            const date = new Date().toISOString().split("T")[0];
            link.download = `${examName}_${date}.xlsx`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            return { success: true, message: "File downloaded successfully" };
          } else {
            return await apiClient.post(`/faculty/reports/assessment-excel`, data);
          }
        } catch (error) {
          logger.error("Error downloading faculty assessment Excel report:", error);
          throw error;
        }
    }

    async getMultiAssessmentReports(ids: number[], filters: any = {}): Promise<any> {
        try {
            const payload = {
                ids,
                batch_year_id: filters.batch_year_id,
                batch_id: filters.batch_id,
            };

            return await apiClient.post(`/faculty/reports/multi-assessment-report-data`, payload);
        } catch (error) {
            logger.error("Error fetching multi-assessment reports:", error);
            throw error;
        }
    }

    async getMultiAssessmentExcelReport(data: any): Promise<any> {
        try {
            if (data.download === true) {
                const blob = await apiClient.postBlob("/faculty/reports/multi-assessment-excel", data);

                const url = window.URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                const date = new Date().toISOString().split("T")[0];
                link.download = `Multi_Assessment_Report_${date}.xlsx`;
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);

                return { success: 1, message: "File downloaded successfully" };
            } else {
                return await apiClient.post("/faculty/reports/multi-assessment-excel", data);
            }
        } catch (error) {
            logger.error("Error fetching multi-assessment Excel report:", error);
            throw error;
        }
    }

    // ─── Exam User Access ───────────────────────────────────────────────

    /**
     * Enable/disable user-level access mode for a batch on an exam
     */
    async enableUserLevelExamAccess(examId: string, data: {
        batch_id: string;
        checked: boolean;
    }): Promise<{ success: number; message: string }> {
        try {
            return await apiClient.post(`/faculty/assessment/${examId}/enable-user-level`, data);
        } catch (error) {
            logger.error("Error enabling user level exam access:", error);
            throw error;
        }
    }

    /**
     * Grant exam access to specific users
     */
    async grantUserExamAccess(examId: string, data: {
        user_ids: string[];
        batch_id: string;
    }): Promise<GrantAccessResponse> {
        try {
            return await apiClient.post(`/faculty/assessment/${examId}/grant-users`, data);
        } catch (error) {
            logger.error("Error granting user exam access:", error);
            throw error;
        }
    }

    /**
     * Revoke exam access from specific users
     */
    async revokeUserExamAccess(examId: string, data: {
        user_ids: string[];
        batch_id: string;
    }): Promise<GrantAccessResponse> {
        try {
            return await apiClient.post(`/faculty/assessment/${examId}/revoke-users`, data);
        } catch (error) {
            logger.error("Error revoking user exam access:", error);
            throw error;
        }
    }

    /**
     * Change user access for an exam
     */
    async changeUserAccessForExam(data: {
        exam_id: string;
        user_id: string;
        batch_id: string;
        access: number;
    }): Promise<any> {
        try {
            return await apiClient.post(`/faculty/assessment/${data.exam_id}/change-user-access`, data);
        } catch (error) {
            logger.error("Error changing user access for exam:", error);
            throw error;
        }
    }

    /**
     * Get list of users with their access status for an exam
     */
    async getExamAccessUsersList(data: {
        exam_id: string;
        batch_id: string;
    }): Promise<ExamAccessUsersResponse> {
        try {
            return await apiClient.post(`/faculty/assessment/${data.exam_id}/users-list`, { batch_id: data.batch_id });
        } catch (error) {
            logger.error("Error fetching exam access users list:", error);
            throw error;
        }
    }

    // ─── Exam Batch Access ───────────────────────────────────────────────

    /**
     * Get category access with exam details
     */
    async getCategoryAccessWithExam(
        categoryId: string,
        examId: string,
    ): Promise<any> {
        try {
            return await apiClient.get(`/faculty/assessments/access/${categoryId}/${examId}`);
        } catch (error) {
            logger.error("Error fetching category access with exam:", error);
            throw error;
        }
    }

    /**
     * Insert or update exam access for a batch
     */
    async insertExamAccess(data: {
        id?: string;
        access_id?: string | null;
        exam_id: string;
        batch_year: string;
        batch: string;
        access?: boolean | number;
        from?: string;
        to?: string;
        fromTime?: string;
        toTime?: string;
        auto_submit?: boolean | number;
        auto_submit_time?: string;
    }): Promise<any> {
        try {
            return await apiClient.post(`/faculty/assessments/access/insert`, data);
        } catch (error) {
            logger.error("Error inserting exam access:", error);
            throw error;
        }
    }

    /**
     * Update global access time for all batches
     */
    async updateGlobalAccessTime(data: {
        exam_id: string;
        from: string;
        to: string;
        fromTime: string;
        toTime: string;
    }): Promise<any> {
        try {
            return await apiClient.post(`/faculty/assessments/access/global-time`, data);
        } catch (error) {
            logger.error("Error updating global access time:", error);
            throw error;
        }
    }

    /**
     * Grant exam access to all batches
     */
    async grantExamAccessToAll(data: {
        exam_id: string;
        batches: Array<{
            batch: string;
            batch_year: string;
        }>;
    }): Promise<any> {
        try {
            return await apiClient.post(`/faculty/assessments/access/grant-all`, data);
        } catch (error) {
            logger.error("Error granting exam access to all:", error);
            throw error;
        }
    }

    /**
     * Grant exam access to selected batches
     */
    async grantExamAccessToBulkBatches(data: {
        category_id: string;
        exam_id: string;
        batches: string[];
    }): Promise<any> {
        try {
            return await apiClient.post(`/faculty/assessments/access/grant-bulk-batches`, data);
        } catch (error) {
            logger.error("Error granting exam access to bulk batches:", error);
            throw error;
        }
    }

    /**
     * Grant exam access to selected users
     */
    async grantExamAccessToBulkUsers(data: {
        category_id: string;
        exam_id: string;
        users: string[];
        type?: "email" | "regd";
    }): Promise<any> {
        try {
            return await apiClient.post(`/faculty/assessments/access/grant-bulk-users`, data);
        } catch (error) {
            logger.error("Error granting exam access to bulk users:", error);
            throw error;
        }
    }

    /**
     * Update access time selection for a specific batch
     */
    async updateAccessTimeSelection(data: {
        id: number;
        from: string;
        to: string;
        fromTime: string;
        toTime: string;
        auto_submit?: boolean | number;
        auto_submit_time?: string;
    }): Promise<any> {
        try {
            return await apiClient.post(`/faculty/assessments/access/time-selection`, data);
        } catch (error) {
            logger.error("Error updating access time selection:", error);
            throw error;
        }
    }

    /**
     * Bulk grant exam access by email or registration numbers
     */
    async grantEmailAccessForFaculty(examId: string, data: {
        batch_id: string;
        identifiers: string;
        isEmail: boolean;
    }): Promise<GrantAccessResponse> {
        try {
            return await apiClient.post(`/faculty/assessment/${examId}/grant-email`, data);
        } catch (error) {
            logger.error("Error bulk granting exam access:", error);
            throw error;
        }
    }
    async forceSubmitAssessmentAttempts(assessmentId: string): Promise<any> {
        // Placeholder for API call
        logger.debug('Force submit attempts for assessment:', assessmentId);
        return { success: 1, message: 'Not implemented yet' };
    }

    async getSummaryReport(ids: string[] | string, filters: any = {}): Promise<any> {
        try {
            return await apiClient.post(`/faculty/reports/summary-report`, { ids, ...filters });
        } catch (error) {
            logger.error("Error fetching faculty summary report:", error);
            throw error;
        }
    }

    async downloadSummaryReportPDF(ids: string[] | string, filters: any = {}): Promise<any> {
        try {
            const blob = await apiClient.postBlob(`/faculty/reports/summary-report-pdf`, { ids, ...filters });

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            const date_str = new Date().toISOString().split("T")[0];
            link.download = `Summary_Report_${date_str}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            logger.error("Error downloading faculty summary report PDF:", error);
            throw error;
        }
    }

    async downloadAssessmentReportPDF(attempt_id: string): Promise<any> {
        try {
            const blob = await apiClient.getBlob(`/faculty/reports/assessment-report-pdf/${attempt_id}`);

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            const date_str = new Date().toISOString().split("T")[0];
            link.download = `Assessment_Report_${attempt_id}_${date_str}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            logger.error("Error downloading faculty assessment report PDF:", error);
            throw error;
        }
    }


    async getParticipationReport(data: any): Promise<any> {
        try {
            if (data.download === true) {
                const blob = await apiClient.postBlob(`/faculty/reports/participation-report`, data);

                const url = window.URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                const examName = "Participation_Report";
                const date = new Date().toISOString().split("T")[0];
                link.download = `${examName}_${date}.xlsx`;
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);

                return { success: 1, message: "File downloaded successfully" };
            } else {
                return await apiClient.post(`/faculty/reports/participation-report`, data);
            }
        } catch (error) {
            logger.error("Error fetching faculty participation report:", error);
            throw error;
        }
    }

    // ================================
    // PRACTICE REPORT METHODS
    // ================================

    async getPracticeReportData(id: string): Promise<any> {
        return apiClient.get(`/faculty/reports/practice/${id}`);
    }

    async getUserPracticeReport(examId: string, userId: string): Promise<any> {
        return apiClient.get(`/faculty/reports/practice/${examId}/${userId}`);
    }

    async getFacultyPracticeSolutions(attemptId: string): Promise<any> {
        try {
            return await apiClient.get(`/faculty/practice/solutions/${attemptId}`);
        } catch (error) {
            logger.error("Error fetching faculty practice solutions:", error);
            throw error;
        }
    }

    async getPracticeSolutions(attemptId: string): Promise<any> {
        try {
            return await apiClient.get(`/faculty/reports/practice/solutions/${attemptId}`);
        } catch (error) {
            // Fallback to /faculty/practice/solutions/
            return await apiClient.get(`/faculty/practice/solutions/${attemptId}`);
        }
    }

    async getExcelPracticeReport(data: {
        practice_id: string;
        batch_year?: string;
        batch?: string;
        from?: string;
        to?: string;
    }): Promise<any> {
        return apiClient.post(`/faculty/reports/practice-excel`, data);
    }

    // ─── Codespace Users ─────────────────────────────────────────────────

    /**
     * Get users list filtered by batch year and/or batch
     */
    async getUsersList(data: {
        batch_year?: string;
        batch?: string;
    }): Promise<{
        success: number;
        message?: string;
        users: Array<{
            id: string;
            firstname: string;
            lastname: string;
            email: string;
            regd: string;
            mobile: string;
            status: string | number;
            city: string;
            state: string;
            country: string;
            type: string;
            batch_id: string;
            batch_name: string;
            batch_year_name: string;
            batch_tag_name: string;
        }>;
    }> {
        try {
            return await apiClient.post("/faculty/usersList", data);
        } catch (error) {
            logger.error("Error fetching users list:", error);
            throw error;
        }
    }

    /**
     * Download class users performance report with 12 aggregated columns
     */
    async downloadClassUsersReport(data: {
        batch_year?: string | string[];
        batch?: string | string[];
        department_id?: string;
    }): Promise<{
        success: number;
        message?: string;
        users: Array<any>;
    }> {
        try {
            return await apiClient.post("/faculty/users/download-report", data);
        } catch (error) {
            logger.error("Error fetching faculty class users report:", error);
            throw error;
        }
    }

    /**
     * Get codespaces list for a specific user
     */
    async getCodespacesListByUser(userId: string): Promise<{
        success: number;
        message?: string;
        data?: {
            user: {
                id: string;
                first_name: string;
                last_name: string;
                email: string;
                regd: string;
                college_name: string;
                batch: string;
                batch_year: string;
            };
            codespaces: Array<{
                id: number;
                name: string;
                parent_id: number | null;
                is_project: number;
            }>;
        };
    }> {
        try {
            return await apiClient.post("/faculty/codespace/list", { user_id: userId });
        } catch (error) {
            logger.error("Error fetching codespaces list:", error);
            throw error;
        }
    }

    /**
     * Get codespace project details
     */
    async getCodespaceProject(userId: string, projectId: string): Promise<{
        success: number;
        message?: string;
        folder?: any;
    }> {
        try {
            return await apiClient.post("/faculty/codespace/project", {
                user_id: userId,
                folder_id: projectId,
            });
        } catch (error) {
            logger.error("Error fetching codespace project:", error);
            throw error;
        }
    }

    // ─── Learning Path (Course) Management ───────────────────────────────

    /**
     * Get all learning paths accessible to faculty
     */
    async getLearningPaths(params?: {
        page?: number;
        limit?: number;
        search?: string;
        published?: boolean;
    }): Promise<{
        success: number;
        message: string;
        data: Array<{
            uuid: string;
            title: string;
            slug: string;
            cover: string;
            image: string;
            duration: string;
            topic_count: number;
            description: string;
            mode: string;
            publish: number;
            created_at: string;
            author: string;
            difficulty: string;
            hour: number;
            minute: number;
            category: string;
        }>;
        pagination?: {
            page: number;
            limit: number;
            total: number;
            totalPages: number;
        };
    }> {
        try {
            const queryParams = new URLSearchParams();
            if (params?.page) queryParams.append("page", params.page.toString());
            if (params?.limit) queryParams.append("limit", params.limit.toString());
            if (params?.search) queryParams.append("search", params.search);
            if (params?.published !== undefined) queryParams.append("published", params.published.toString());

            const qs = queryParams.toString() ? `?${queryParams.toString()}` : "";
            return await apiClient.get(`/faculty/learning-paths${qs}`);
        } catch (error) {
            logger.error("Error fetching learning paths:", error);
            throw error;
        }
    }

    /**
     * Get single learning path by UUID
     */
    async getLearningPathById(uuid: string): Promise<{
        success: number;
        message: string;
        data: {
            id: number;
            uuid: string;
            title: string;
            slug: string;
            description: string;
            cover: string;
            image: string;
            video: string;
            difficulty: string;
            author: string;
            duration: string;
            hour: number;
            minute: number;
            mode: string;
            requirements: string;
            learn: string;
            topic_count: number;
            price: number;
            discount: number;
            subscription: number;
            completion_certificate: number;
            video_forward?: number;
            retake_mode: number;
            best_seller: number;
            publish: number;
            category: string;
            tags: string[];
            topics: Array<{
                id: number;
                uuid: string;
                title: string;
                slug: string;
                description: string;
                sort_order: number;
                assessment_id: string;
                is_exam: number;
            }>;
        };
    }> {
        try {
            return await apiClient.get(`/faculty/learning-paths/${uuid}`);
        } catch (error) {
            logger.error("Error fetching learning path:", error);
            throw error;
        }
    }

    /**
     * Create a new learning path
     */
    async createLearningPath(data: {
        title: string;
        slug?: string;
        description?: string;
        cover?: string;
        image?: string;
        video?: string;
        difficulty?: string;
        author?: string;
        duration?: string;
        hour?: number;
        minute?: number;
        mode?: string;
        requirements?: string;
        learn?: string;
        topic_count?: number;
        price?: number;
        discount?: number;
        subscription?: number;
        completion_certificate?: number;
        video_forward?: number;
        retake_mode?: number;
        best_seller?: number;
    }): Promise<{
        success: number;
        message: string;
        data?: { uuid: string; id: number };
    }> {
        try {
            return await apiClient.post("/faculty/learning-paths", data);
        } catch (error) {
            logger.error("Error creating learning path:", error);
            throw error;
        }
    }

    /**
     * Update a learning path
     */
    async updateLearningPath(data: {
        uuid: string;
        title?: string;
        slug?: string;
        description?: string;
        cover?: string;
        image?: string;
        video?: string;
        difficulty?: string;
        author?: string;
        duration?: string;
        hour?: number;
        minute?: number;
        mode?: string;
        requirements?: string;
        learn?: string;
        topic_count?: number;
        price?: number;
        discount?: number;
        subscription?: number;
        completion_certificate?: number;
        video_forward?: number;
        retake_mode?: number;
        best_seller?: number;
        publish?: number;
    }): Promise<{
        success: number;
        message: string;
    }> {
        try {
            return await apiClient.put("/faculty/learning-paths", data);
        } catch (error) {
            logger.error("Error updating learning path:", error);
            throw error;
        }
    }

    /**
     * Delete a learning path
     */
    async deleteLearningPath(uuid: string): Promise<{
        success: number;
        message: string;
    }> {
        try {
            return await apiClient.delete(`/faculty/learning-paths/${uuid}`);
        } catch (error) {
            logger.error("Error deleting learning path:", error);
            throw error;
        }
    }

    /**
     * Get topics for a learning path
     */
    async getTopicsByLearningPath(uuid: string): Promise<{
        success: number;
        message: string;
        data: Array<{
            id: number;
            uuid: string;
            title: string;
            slug: string;
            description: string;
            sort_order: number;
            assessment_id: string;
            is_exam: number;
            content_count: number;
        }>;
    }> {
        try {
            return await apiClient.get(`/faculty/learning-paths/${uuid}/topics`);
        } catch (error) {
            logger.error("Error fetching topics:", error);
            throw error;
        }
    }

    /**
     * Create a new topic
     */
    async createTopic(data: {
        learning_path_uuid: string;
        title: string;
        slug?: string;
        description?: string;
        sort_order?: number;
        assessment_id?: string;
        is_exam?: boolean;
    }): Promise<{
        success: number;
        message: string;
        data?: { uuid: string; id: number };
    }> {
        try {
            return await apiClient.post("/faculty/topics", data);
        } catch (error) {
            logger.error("Error creating topic:", error);
            throw error;
        }
    }

    /**
     * Update a topic
     */
    async updateTopic(data: {
        uuid: string;
        title?: string;
        slug?: string;
        description?: string;
        sort_order?: number;
        assessment_id?: string;
        is_exam?: boolean;
        publish?: number;
    }): Promise<{
        success: number;
        message: string;
    }> {
        try {
            return await apiClient.put("/faculty/topics", data);
        } catch (error) {
            logger.error("Error updating topic:", error);
            throw error;
        }
    }

    /**
     * Delete a topic
     */
    async deleteTopic(uuid: string): Promise<{
        success: number;
        message: string;
    }> {
        try {
            return await apiClient.delete(`/faculty/topics/${uuid}`);
        } catch (error) {
            logger.error("Error deleting topic:", error);
            throw error;
        }
    }

    /**
     * Get content for a topic
     */
    async getTopicContent(uuid: string): Promise<{
        success: number;
        message: string;
        data: Array<{
            id: number;
            uuid: string;
            title: string;
            description: string;
            content_type: string;
            content_url: string;
            video_url: string;
            duration: string;
            sort_order: number;
            topic_id: number;
            knowledge_check_id: string;
            assessment_id: string;
        }>;
    }> {
        try {
            return await apiClient.get(`/faculty/topics/${uuid}/content`);
        } catch (error) {
            logger.error("Error fetching topic content:", error);
            throw error;
        }
    }

    /**
     * Create content for a topic
     */
    async createContent(data: {
        topic_uuid: string;
        title: string;
        description?: string;
        content_type?: string;
        content_url?: string;
        video_url?: string;
        duration?: string;
        sort_order?: number;
        knowledge_check_id?: string;
        assessment_id?: string;
    }): Promise<{
        success: number;
        message: string;
        data?: { uuid: string; id: number };
    }> {
        try {
            return await apiClient.post("/faculty/content", data);
        } catch (error) {
            logger.error("Error creating content:", error);
            throw error;
        }
    }

    /**
     * Update content
     */
    async updateContent(data: {
        uuid: string;
        title?: string;
        description?: string;
        content_type?: string;
        content_url?: string;
        video_url?: string;
        duration?: string;
        sort_order?: number;
        knowledge_check_id?: string;
        assessment_id?: string;
        publish?: number;
    }): Promise<{
        success: number;
        message: string;
    }> {
        try {
            return await apiClient.put("/faculty/content", data);
        } catch (error) {
            logger.error("Error updating content:", error);
            throw error;
        }
    }

    /**
     * Delete content
     */
    async deleteContent(uuid: string): Promise<{
        success: number;
        message: string;
    }> {
        try {
            return await apiClient.delete(`/faculty/content/${uuid}`);
        } catch (error) {
            logger.error("Error deleting content:", error);
            throw error;
        }
    }

    // ─── Course Access Management ─────────────────────────────────────

    /**
     * Get course access (batch assignments) for a course
     */
    async getCourseAccess(uuid: string): Promise<{
        success: number;
        message: string;
        data: Array<{
            id: number;
            learning_path_id: string;
            batch: string;
            batch_year: string;
            access: number;
            batch_name: string;
            batch_year_name: string;
            created_at: string;
            updated_at: string;
        }>;
        course: {
            id: number;
            uuid: string;
            title: string;
        };
    }> {
        try {
            return await apiClient.get(`/faculty/courses/${uuid}/access`);
        } catch (error) {
            logger.error("Error fetching course access:", error);
            throw error;
        }
    }

    /**
     * Insert course access for a batch
     */
    async insertCourseAccess(uuid: string, data: {
        batch_id: string;
        batch_year_id: string;
        access?: boolean;
    }): Promise<{
        success: number;
        message: string;
    }> {
        try {
            return await apiClient.post(`/faculty/courses/${uuid}/access/insert`, data);
        } catch (error) {
            logger.error("Error inserting course access:", error);
            throw error;
        }
    }

    /**
     * Update course access
     */
    async updateCourseAccess(uuid: string, data: {
        id: number;
        access: boolean;
    }): Promise<{
        success: number;
        message: string;
    }> {
        try {
            return await apiClient.post(`/faculty/courses/${uuid}/access/update`, data);
        } catch (error) {
            logger.error("Error updating course access:", error);
            throw error;
        }
    }

    /**
     * Delete course access
     */
    async deleteCourseAccess(uuid: string, id: number): Promise<{
        success: number;
        message: string;
    }> {
        try {
            return await apiClient.post(`/faculty/courses/${uuid}/access/delete`, { id });
        } catch (error) {
            logger.error("Error deleting course access:", error);
            throw error;
        }
    }

    /**
     * Get student progress for a course
     */
    async getStudentProgress(uuid: string): Promise<{
        success: number;
        message: string;
        data: Array<{
            id: string;
            uuid: string;
            first_name: string;
            last_name: string;
            email: string;
            regd: string;
            batch_name: string;
            batch_year_name: string;
            completed_content: number;
            total_content: number;
        }>;
    }> {
        try {
            return await apiClient.get(`/faculty/learning-paths/${uuid}/students`);
        } catch (error) {
            logger.error("Error fetching student progress:", error);
            throw error;
        }
    }

    /**
     * Get detailed progress for a specific student in a course
     */
    async getStudentDetail(uuid: string, userId: string): Promise<{
        success: number;
        message: string;
        data: {
            student: {
                id: number;
                uuid: string;
                first_name: string;
                last_name: string;
                email: string;
                regd: string;
                batch_name: string;
                batch_year_name: string;
            };
            completed_content: Array<{
                id: number;
                content_id: string;
                completed_at: string;
                content_title: string;
                content_uuid: string;
                topic_title: string;
            }>;
            all_content: Array<{
                uuid: string;
                title: string;
                topic_title: string;
                duration: string;
                completed_at: string | null;
            }>;
        };
    }> {
        try {
            return await apiClient.get(`/faculty/learning-paths/${uuid}/student/${userId}`);
        } catch (error) {
            logger.error("Error fetching student detail:", error);
            throw error;
        }
    }

    /**
     * Get institution colors for the faculty's institution
     */
    async getInstitutionColors(): Promise<{
        success: number;
        message: string;
        data?: {
            primary_colour: string | null;
            secondary_colour: string | null;
            institution_id: string;
        };
    }> {
        try {
            return await apiClient.get("/faculty/institution/colors");
        } catch (error) {
            logger.error("Error fetching faculty institution colors:", error);
            throw error;
        }
    }

    // ===== VERSANT ACCESS MANAGEMENT =====

    async getVersantAccessCategories(): Promise<any> {
        try {
            return await apiClient.get("/faculty/versant/categories");
        } catch (error) {
            logger.error("Error fetching Versant access categories:", error);
            throw error;
        }
    }

    async getVersantAccessAssessments(): Promise<any> {
        try {
            return await apiClient.get("/faculty/versant/assessments");
        } catch (error) {
            logger.error("Error fetching Versant access assessments:", error);
            throw error;
        }
    }

    async getVersantCategoryAccess(id: string): Promise<any> {
        try {
            return await apiClient.get(`/faculty/versant/category-access/${id}`);
        } catch (error) {
            logger.error("Error fetching Versant category access:", error);
            throw error;
        }
    }

    async createVersantCategoryAccess(data: {
        category_id: string;
        batch_year: string;
        batches?: string[];
    }): Promise<any> {
        try {
            return await apiClient.post("/faculty/versant/category-access/create", data);
        } catch (error) {
            logger.error("Error creating Versant category access:", error);
            throw error;
        }
    }

    async updateVersantCategoryAccess(data: {
        id: number;
        category_id: string;
        batch_year: string;
        batches?: string[];
    }): Promise<any> {
        try {
            return await apiClient.post("/faculty/versant/category-access/update", data);
        } catch (error) {
            logger.error("Error updating Versant category access:", error);
            throw error;
        }
    }

    async deleteVersantCategoryAccess(data: {
        id: number;
        category_id: string;
    }): Promise<any> {
        try {
            return await apiClient.post("/faculty/versant/category-access/delete", data);
        } catch (error) {
            logger.error("Error deleting Versant category access:", error);
            throw error;
        }
    }

    async getVersantAssessmentAccess(category: string, id: string): Promise<any> {
        try {
            return await apiClient.get(`/faculty/versant/assessment-access/${category}/${id}`);
        } catch (error) {
            logger.error("Error fetching Versant assessment access:", error);
            throw error;
        }
    }

    async updateVersantAssessmentAccess(data: {
        exam_id: string;
        batch_year: string;
        batch: string;
        access: boolean;
    }): Promise<any> {
        try {
            return await apiClient.post("/faculty/versant/assessment-access/update", data);
        } catch (error) {
            logger.error("Error updating Versant assessment access:", error);
            throw error;
        }
    }

    /**
     * Update Versant assessment schedule / time selection for a batch
     */
    async updateVersantAssessmentTime(data: {
        id: number;
        from?: string | null;
        to?: string | null;
        fromTime?: string | null;
        toTime?: string | null;
        auto_submit?: 0 | 1;
        auto_submit_time?: string | number | null;
    }): Promise<any> {
        try {
            return await apiClient.post("/faculty/versant/assessment-access/time-selection", data);
        } catch (error) {
            logger.error("Error updating Versant assessment time selection:", error);
            throw error;
        }
    }

    /**
     * Update Versant assessment schedule globally for all batches
     */
    async updateVersantGlobalAccessTime(data: {
        exam_id: string;
        from?: string | null;
        to?: string | null;
        fromTime?: string | null;
        toTime?: string | null;
    }): Promise<any> {
        try {
            return await apiClient.post("/faculty/versant/assessment-access/global-time", data);
        } catch (error) {
            logger.error("Error updating Versant global access time:", error);
            throw error;
        }
    }

    /**
     * Bulk grant Versant assessment access to all batches
     */
    async grantVersantExamAccessToAll(data: {
        exam_id: string;
        batches: Array<{ batch: string; batch_year: string }>;
    }): Promise<any> {
        try {
            return await apiClient.post("/faculty/versant/assessment-access/grant-all", data);
        } catch (error) {
            logger.error("Error granting Versant assessment access to all:", error);
            throw error;
        }
    }

    /**
     * Update Versant user level access flag
     */
    async updateVersantExamUserLevelAccess(data: {
        checked: 0 | 1;
        access_id: number;
        batch: string;
        exam_id: string;
    }): Promise<any> {
        try {
            return await apiClient.post("/faculty/versant/assessment-access/user-level/update", data);
        } catch (error) {
            logger.error("Error updating Versant user level access:", error);
            throw error;
        }
    }

    /**
     * Get students in a batch with their access state for Versant exam
     */
    async getVersantExamAccessUsersList(data: {
        exam_id: string;
        batch_id: string;
    }): Promise<any> {
        try {
            return await apiClient.post("/faculty/versant/assessment-access/users/list", data);
        } catch (error) {
            logger.error("Error fetching Versant exam access users list:", error);
            throw error;
        }
    }

    /**
     * Toggle student access for Versant exam
     */
    async changeVersantUserAccessForExam(data: {
        exam_id: string;
        user_id: string;
        batch_id: string;
        checked: 0 | 1;
    }): Promise<any> {
        try {
            return await apiClient.post("/faculty/versant/assessment-access/user/toggle", data);
        } catch (error) {
            logger.error("Error changing Versant user access:", error);
            throw error;
        }
    }

    /**
     * Grant Versant assessment access to users by email or regd numbers
     */
    async grantVersantEmailAccess(data: {
        emails: string;
        isEmail?: number;
        batch: string;
        exam_id: string;
        access_id?: number | null;
    }): Promise<any> {
        try {
            return await apiClient.post("/faculty/versant/assessment-access/grant-email", data);
        } catch (error) {
            logger.error("Error granting Versant email access:", error);
            throw error;
        }
    }

    /**
     * Toggle freeze status for Versant exam
     */
    async freezeVersantTest(data: {
        exam_id: string;
        batch_year: string;
        batch: string;
        freeze: boolean | number;
    }): Promise<any> {
        try {
            return await apiClient.post("/faculty/versant/assessment-access/freeze-test", data);
        } catch (error) {
            logger.error("Error freezing/unfreezing Versant test:", error);
            throw error;
        }
    }

    // ===== VERSANT REPORT OPERATIONS =====

    async getVersantReportsByID(
        id: string,
        userId: string,
    ): Promise<any> {
        try {
            return await apiClient.post(`/faculty/versant/reports/${id}`, {
                user_id: userId,
            });
        } catch (error) {
            logger.error("Error fetching Versant report by ID:", error);
            throw error;
        }
    }

    async getUserVersantReport(
        examId: string,
        userId: string,
    ): Promise<any> {
        try {
            return await apiClient.get(`/faculty/versant/reports/${examId}/${userId}`);
        } catch (error) {
            logger.error("Error fetching user Versant report:", error);
            throw error;
        }
    }

    async getVersantOverallReport(attemptId: string): Promise<any> {
        try {
            return await apiClient.get(`/faculty/versant/reports/overall/${attemptId}`);
        } catch (error) {
            logger.error("Error fetching Versant overall report:", error);
            throw error;
        }
    }

    async getVersantSolutions(attemptId: string): Promise<any> {
        try {
            return await apiClient.get(`/faculty/versant/reports/solutions/${attemptId}`);
        } catch (error) {
            logger.error("Error fetching Versant solutions:", error);
            throw error;
        }
    }

    async getExcelVersantReport(data: any): Promise<any> {
        try {
            return await apiClient.post("/faculty/versant/reports/excel", data);
        } catch (error) {
            logger.error("Error exporting Excel Versant report:", error);
            throw error;
        }
    }

    async getUserFullReport(userId: string): Promise<any> {
        try {
            const res: any = await apiClient.get(`/faculty/users/full-report/${userId}`);
            if (res && (res.success === 1 || res.success === true) && res.data) {
                return res;
            }
            const instRes: any = await apiClient.get(`/institution/users/full-report/${userId}`);
            if (instRes && (instRes.success === 1 || instRes.success === true) && instRes.data) {
                return instRes;
            }
            const vendorRes: any = await apiClient.get(`/vendor/users/full-report/${userId}`);
            if (vendorRes && (vendorRes.success === 1 || vendorRes.success === true)) {
                return vendorRes;
            }
            return res || { success: false, data: { trainings: [] } };
        } catch (error) {
            try {
                const instRes: any = await apiClient.get(`/institution/users/full-report/${userId}`);
                if (instRes && (instRes.success === 1 || instRes.success === true)) {
                    return instRes;
                }
                return await apiClient.get(`/vendor/users/full-report/${userId}`);
            } catch (fallbackError) {
                logger.error("Error fetching faculty user full report:", fallbackError);
                return { success: false, data: { trainings: [] } };
            }
        }
    }

    /**
     * Get faculty profile
     */
    async getProfile(): Promise<{
        success: number;
        message: string;
        data?: {
            id: string;
            first_name: string;
            last_name: string;
            email: string;
            mobile: string | null;
            institution_id: string;
            created_at: string;
        };
    }> {
        try {
            return await apiClient.get("/faculty/profile");
        } catch (error) {
            logger.error("Error fetching faculty profile:", error);
            throw error;
        }
    }

    /**
     * Update faculty profile
     */
    async updateProfile(data: {
        first_name?: string;
        last_name?: string;
        email?: string;
        mobile?: string;
    }): Promise<{
        success: number;
        message: string;
        data?: {
            id: string;
            first_name: string;
            last_name: string;
            email: string;
            mobile: string | null;
            institution_id: string;
            created_at: string;
        };
    }> {
        try {
            return await apiClient.put("/faculty/profile/update", data);
        } catch (error) {
            logger.error("Error updating faculty profile:", error);
            throw error;
        }
    }

    /**
     * Update faculty password
     */
    async updatePassword(data: {
        current_password: string;
        new_password: string;
        confirm_password: string;
    }): Promise<{
        success: number;
        message: string;
    }> {
        try {
            return await apiClient.post("/faculty/profile/password", data);
        } catch (error) {
            logger.error("Error updating faculty password:", error);
            throw error;
        }
    }

    async getAssessmentCategories(): Promise<any> {
        try {
            return await apiClient.get("/faculty/assessments/categories");
        } catch (error) {
            logger.error("Error fetching faculty assessment categories:", error);
            throw error;
        }
    }

    // ─── Category Access Management ────────────────────────────────────────

    async getCategoryDetailsById(categoryId: string): Promise<any> {
        try {
            return await apiClient.get(`/faculty/category/access/${categoryId}`);
        } catch (error) {
            logger.error("Error fetching category details:", error);
            throw error;
        }
    }

    async getCategoryAccessList(categoryId: string): Promise<any> {
        try {
            return await apiClient.get(`/faculty/category/access/list/${categoryId}`);
        } catch (error) {
            logger.error("Error fetching category access list:", error);
            throw error;
        }
    }

    async createCategoryAccess(data: any): Promise<any> {
        try {
            return await apiClient.post("/faculty/category/access/create", data);
        } catch (error) {
            logger.error("Error creating category access:", error);
            throw error;
        }
    }

    async updateCategoryAccess(data: { id: string | number; batch_year_id?: string; batch_id?: string[] }): Promise<any> {
        try {
            return await apiClient.post("/faculty/category/access/update", data);
        } catch (error) {
            logger.error("Error updating category access:", error);
            throw error;
        }
    }

    async deleteCategoryAccess(data: { id: string | number }): Promise<any> {
        try {
            return await apiClient.post("/faculty/category/access/delete", data);
        } catch (error) {
            logger.error("Error deleting category access:", error);
            throw error;
        }
    }

    // User Summary Methods for User View
    async getUserAssessmentActivity(user_id: string): Promise<any> {
        try {
            const res: any = await apiClient.get(`/faculty/user/${user_id}/assessment-activity`);
            if (res && (res.success === 1 || res.success === true) && (res.score_distribution || res.data?.score_distribution)) {
                return res;
            }
            return await apiClient.get(`/vendor/user/${user_id}/assessment-activity`);
        } catch (error) {
            logger.error("Error fetching user assessment activity:", error);
            try {
                return await apiClient.get(`/vendor/user/${user_id}/assessment-activity`);
            } catch (err) {
                return { success: 0, student_exams: [] };
            }
        }
    }

    async getUserAssessmentsSummary(user_id: string): Promise<any> {
        try {
            return await apiClient.get(`/faculty/user/${user_id}/assessments-summary`);
        } catch (error) {
            logger.error("Error fetching user assessments summary:", error);
            throw error;
        }
    }

    async getUserPracticeSummary(user_id: string): Promise<any> {
        try {
            return await apiClient.get(`/faculty/user/${user_id}/practice-summary`);
        } catch (error) {
            logger.error("Error fetching user practice summary:", error);
            throw error;
        }
    }

    async getUserVersantSummary(user_id: string): Promise<any> {
        try {
            return await apiClient.get(`/faculty/user/${user_id}/versant-summary`);
        } catch (error) {
            logger.error("Error fetching user versant summary:", error);
            throw error;
        }
    }

    async getUserCoursesSummary(user_id: string): Promise<any> {
        try {
            return await apiClient.get(`/faculty/user/${user_id}/courses-summary`);
        } catch (error) {
            logger.error("Error fetching user courses summary:", error);
            throw error;
        }
    }

    async getUserTrainingActivity(user_id: string): Promise<any> {
        try {
            return await apiClient.get(`/faculty/user/${user_id}/training-activity`);
        } catch (error) {
            logger.error("Error fetching user training activity (faculty):", error);
            return { success: 0, data: [] };
        }
    }

    // Aliases for unified interface usage
    async getBatchYearList(): Promise<any> {
        return this.getBatchYears();
    }
    async getBatchesByBatchYear(yearId: string): Promise<any> {
        return this.getBatchesByYear(yearId);
    }

    /**
     * Get faculty assessment report leaderboard rankings
     * GET /faculty/reports/assessment-leaderboard/:examId
     */
    async getAssessmentLeaderboard(examId: string, params?: Record<string, any>): Promise<any> {
        try {
            const query = params ? `?${new URLSearchParams(params).toString()}` : "";
            return await apiClient.get(`/faculty/reports/assessment-leaderboard/${examId}${query}`);
        } catch (error) {
            logger.error("Error fetching faculty assessment leaderboard:", error);
            throw error;
        }
    }

    /**
     * Get assigned assessment template for faculty
     * GET /faculty/assigned-template
     */
    async getAssignedTemplate(): Promise<any> {
        try {
            return await apiClient.get("/faculty/assigned-template");
        } catch (error) {
            logger.error("Error fetching faculty assigned template:", error);
            throw error;
        }
    }

    /**
     * Get assigned practice template for faculty
     * GET /faculty/practice-template
     */
    async getPracticeTemplate(): Promise<any> {
        try {
            return await apiClient.get("/faculty/practice-template");
        } catch (error) {
            logger.error("Error fetching faculty practice template:", error);
            throw error;
        }
    }

    /**
     * Get faculty reports centre overview summary
     * GET or POST /faculty/reports/summary
     */
    async getReportsSummary(payload?: Record<string, any>): Promise<any> {
        try {
            return await apiClient.post("/faculty/reports/summary", payload || {});
        } catch (error) {
            logger.error("Error fetching faculty reports summary:", error);
            throw error;
        }
    }

    /**
     * Get subject tags for faculty
     * GET /faculty/subject-tags
     */
    async getSubjectTags(): Promise<any> {
        try {
            return await apiClient.get("/faculty/subject-tags");
        } catch (error) {
            logger.error("Error fetching faculty subject tags:", error);
            throw error;
        }
    }

    /**
     * Get Student Performance Report for faculty
     * POST /faculty/reports/student-performance
     */
    async getStudentPerformanceReport(payload: Record<string, any>): Promise<any> {
        try {
            return await apiClient.post("/faculty/reports/student-performance", payload || {});
        } catch (error) {
            logger.error("Error fetching faculty student performance report:", error);
            throw error;
        }
    }

    /**
     * Download Student Performance Report for faculty
     * POST /faculty/reports/student-performance/download
     */
    async downloadStudentPerformanceReport(payload: Record<string, any>): Promise<any> {
        try {
            return await apiClient.postBlob("/faculty/reports/student-performance/download", payload || {});
        } catch (error) {
            logger.error("Error downloading faculty student performance report:", error);
            throw error;
        }
    }

    /**
     * Get Candidate 360 Master Report for faculty
     * POST /faculty/analytics/candidate-360-report
     */
    async getCandidate360Report(payload: Record<string, any>): Promise<any> {
        try {
            return await apiClient.post("/faculty/analytics/candidate-360-report", payload || {});
        } catch (error) {
            logger.error("Error fetching faculty candidate 360 report:", error);
            throw error;
        }
    }

    /**
     * Get Student Coding Profiles Report for faculty
     * POST /faculty/reports/coding-profiles
     */
    async getCodingProfilesReport(payload: Record<string, any>): Promise<any> {
        try {
            return await apiClient.post("/faculty/reports/coding-profiles", payload || {});
        } catch (error) {
            logger.error("Error fetching faculty coding profiles report:", error);
            throw error;
        }
    }

    /**
     * Get trainings drilldown report for faculty
     * POST /faculty/analytics/trainings/drilldown
     */
    async getTrainingsDrilldown(payload: Record<string, any>): Promise<any> {
        try {
            return await apiClient.post("/faculty/analytics/trainings/drilldown", payload || {});
        } catch (error) {
            logger.error("Error fetching faculty trainings drilldown:", error);
            throw error;
        }
    }

    /**
     * Get faculty historical analytics dashboard (Overview Metric Cards)
     * POST /faculty/analytics/history
     */
    async getHistoricalAnalytics(filter_parameters?: Record<string, any>): Promise<any> {
        try {
            return await apiClient.post("/faculty/analytics/history", filter_parameters || {});
        } catch (error_object) {
            logger.error("Error fetching faculty historical analytics:", error_object);
            throw error_object;
        }
    }

    /**
     * Get faculty live monitor telemetry metrics
     * GET /faculty/analytics/live-metrics
     */
    async getLiveMonitorMetrics(): Promise<any> {
        try {
            return await apiClient.get("/faculty/analytics/live-metrics");
        } catch (error_object) {
            logger.error("Error fetching faculty live monitor metrics:", error_object);
            throw error_object;
        }
    }

    /**
     * Get faculty recent activities feed
     * GET /faculty/analytics/recent-activities
     */
    async getRecentActivities(limit: number = 20, time_window_minutes?: string): Promise<any> {
        try {
            const query_string = time_window_minutes ? `?limit=${limit}&time_window=${time_window_minutes}` : `?limit=${limit}`;
            return await apiClient.get(`/faculty/analytics/recent-activities${query_string}`);
        } catch (error_object) {
            logger.error("Error fetching faculty recent activities:", error_object);
            throw error_object;
        }
    }
}

export const facultyService = new FacultyService();


